import { useEffect, useState, type RefObject } from 'react'
import { presentedVideoState, type PresentedVideoState } from '../services/presented-video-state'

/**
 * `loadstart` is a new stream attached (loading again), `loadeddata` its first
 * frame (live), `emptied` the stream cleared.
 */
const EVENTS = ['play', 'pause', 'emptied', 'loadstart', 'loadeddata'] as const

/**
 * Follows the presented `<video>` through presented-video-state.
 *
 * The element is read once, on mount, so call this from something that mounts
 * with the element — the stage that holds it, not a parent that outlives it.
 */
export function usePresentedVideoState(videoRef: RefObject<HTMLVideoElement | null>): PresentedVideoState {
  // Loading to begin with: the element has just mounted and drawn nothing yet.
  const [state, setState] = useState<PresentedVideoState>('loading')

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const check = () => setState(presentedVideoState(video))
    check()
    for (const e of EVENTS) video.addEventListener(e, check)
    // Also check periodically in case the stream ends without an event.
    const interval = setInterval(check, 1000)
    return () => {
      for (const e of EVENTS) video.removeEventListener(e, check)
      clearInterval(interval)
    }
  }, [videoRef])

  return state
}
